import React, { useState, useRef, useEffect } from 'react';
import { Control, Controller, FieldError } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, ChevronDown } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { cn } from '@/utils';

interface CustomInputProps {
  name: string;
  control: Control<any>;
  label?: string;
  errorClassname?: string;
  type?: string;
  placeholder?: string;
  labelClassName?: string;
  inputClassName?: string;
  options?: { label: string; value: string | number }[];
  disabled?: boolean;
  showPreviewButton?: boolean;
  previewNewUrl?: string;
  maxLength?: number;
  onFileSelect?: (e: any) => void;
  icon?: React.ReactNode;
  /** Bump this value (e.g. a counter) to force the password to reveal, e.g. after auto-generating it. */
  revealTrigger?: number;
  [key: string]: any;
}

const CustomInput: React.FC<CustomInputProps> = ({
  name,
  control,
  label,
  type = 'text',
  placeholder,
  labelClassName = '',
  inputClassName = '',
  disabled = false,
  options = [],
  showPreviewButton = false,
  previewNewUrl,
  maxLength,
  onFileSelect,
  icon,
  revealTrigger,
  errorClassname,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [openSelect, setOpenSelect] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (revealTrigger !== undefined) {
      setShowPassword(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealTrigger]);

  const isPassword = type === 'password';
  const isSelect = type === 'select';
  const isFile = type === 'file';
  const isPhone = type === 'phone';

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={name} className={cn('text-sm', labelClassName)}>
          {label}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const error: FieldError | undefined = fieldState.error;

          /** PHONE INPUT */
          if (isPhone) {
            return (
              <>
                <PhoneInput
                  {...field}
                  country="in"
                  value={field.value}
                  onChange={(v) => field.onChange(v.startsWith('+') ? v : '+' + v)}
                  inputClass={`!w-full !h-[38px] ${
                    error ? 'border-destructive' : ''
                  } ${inputClassName}`}
                  containerClass="w-full text-black"
                  placeholder={placeholder}
                  enableSearch
                />
                {error && <p className="text-xs text-destructive">{error.message}</p>}
              </>
            );
          }

          /** SELECT INPUT */
          if (isSelect) {
            const selected = options.find((o) => o.value === field.value);

            return (
              <div className="relative">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => !disabled && setOpenSelect(!openSelect)}
                  className={cn(
                    'border rounded-md px-3 py-2 flex justify-between items-center h-[38px] w-full bg-white text-black',
                    error ? 'border-destructive' : 'border-gray-300',
                    inputClassName,
                    disabled && 'cursor-not-allowed opacity-60'
                  )}
                >
                  <span>{selected ? selected.label : placeholder}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>

                {openSelect && !disabled && (
                  <div className="absolute mt-1 w-full bg-white text-black  border-green rounded-md shadow-md z-10 max-h-40 overflow-auto">
                    {options.map((opt, idx) => (
                      <div
                        key={idx}
                        className="px-3 py-2  cursor-pointer border-b"
                        onClick={() => {
                          field.onChange(opt.value);
                          setOpenSelect(false);
                        }}
                      >
                        {opt.label}
                      </div>
                    ))}
                  </div>
                )}

                {error && (
                  <p className={`text-xs text-destructive ${errorClassname}`}>{error.message}</p>
                )}
              </div>
            );
          }

          /** FILE INPUT */
          if (isFile) {
            return (
              <div>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => fileRef.current?.click()}
                  className={cn(
                    'w-full border border-gray-300 bg-white text-black rounded-md py-2 h-[38px] text-left px-4',
                    'truncate overflow-hidden whitespace-nowrap',
                    inputClassName,
                    disabled && 'cursor-not-allowed opacity-60'
                  )}
                >
                  {field.value
                    ? typeof field.value === 'string'
                      ? field.value.length > 50
                        ? field.value.slice(0, 50) + '...'
                        : field.value
                      : field.value?.name.length > 25
                        ? field.value.name.slice(0, 25) + '...'
                        : field.value.name
                    : 'Upload'}
                </button>

                <input
                  type="file"
                  ref={fileRef}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || '';
                    field.onChange(file);
                    onFileSelect?.(e);
                  }}
                />

                {showPreviewButton && previewNewUrl && (
                  <a
                    href={previewNewUrl}
                    target="_blank"
                    className="text-blue text-sm underline mt-1 block"
                  >
                    Preview File
                  </a>
                )}

                {error && <p className="text-xs text-destructive">{error.message}</p>}
              </div>
            );
          }

          return (
            <>
              <div className="relative">
                {icon && (
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    {icon}
                  </span>
                )}
                <Input
                  {...field}
                  value={field.value ?? ''}
                  id={name}
                  type={isPassword ? (showPassword ? 'text' : 'password') : type}
                  placeholder={placeholder}
                  disabled={disabled}
                  maxLength={maxLength}
                  autoComplete={isPassword ? 'new-password' : 'off'}
                  className={cn(
                    '!h-[38px] text-black',
                    error ? 'border-destructive' : '',
                    icon && 'pl-10',
                    inputClassName,
                    disabled && 'opacity-80 cursor-not-allowed'
                  )}
                  onChange={(e) => {
                    field.onChange(e);
                    props?.onChange?.(e);
                  }}
                />

                {isPassword && (
                  <button
                    type="button"
                    className="absolute right-1 top-0 h-full px-2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <Eye className="text-gray" />
                    ) : (
                      <EyeOff className="text-gray" />
                    )}
                  </button>
                )}
              </div>

              {error && (
                <p className={`text-xs text-[#B50002] ${errorClassname}`}>{error.message}</p>
              )}
            </>
          );
        }}
      />
    </div>
  );
};

export default CustomInput;
